import Head from 'next/head'
import Script from 'next/script'
import { FaBox, FaCalendarCheck, FaBell, FaPlusCircle, FaClock, FaClipboardCheck, FaComments, FaExclamationTriangle } from 'react-icons/fa'
import { LegalPageLayout, LegalSection, LegalHighlight } from '../../components/index/LegalPageLayout'

export default function PackagedServiceForServiceProvider() {
  return (
    <>
      <Head>
        <title>Terms on Packaged Service for Service Provider | StringsSG</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"/>
        <meta name="description" content="These terms apply to service providers on StringsSG platform who are assigned a packaged service from a requester."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:url" content="https://www.stringssg.com/terms/packaged-service-for-service-provider" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Terms of Packaged Service for Service Provider | StringsSG"/>
        <meta property="og:description" content="These terms apply to service providers on StringsSG platform who are assigned a packaged service from a requester." />
        <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/terms/packaged-service-for-service-provider"/>
        <meta name="twitter:title" content="Terms of Packaged Service for Service Provider | StringsSG"/> 
        <meta name="twitter:description" content="These terms apply to service providers on StringsSG platform who are assigned a packaged service from a requester."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/terms/packaged-service-for-service-provider"/>
      </Head>
      
      <LegalPageLayout
        title="Packaged Service Terms"
        subtitle="Terms on Packaged Service for Service Provider"
        icon={FaBox}
        iconBgColor="from-indigo-500 to-purple-600"
        heroBgColor="from-slate-900 via-indigo-900 to-purple-900"
      >
        <LegalHighlight color="blue">
          <div className="flex items-start gap-3">
            <FaBox className="text-indigo-600 text-xl mt-1 shrink-0" />
            <div>
              <p className="font-semibold mb-2">When is it applicable?</p>
              <p>If you have accepted and have been paid for any type of service package like annual service package, sessions package or hourly package etc. originating from a client request on the StringsSG platform.</p>
            </div>
          </div>
        </LegalHighlight>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <FaClipboardCheck className="text-green-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">1. Closing the Job (First Service)</h2>
          </div>
          <p className="text-slate-600">
            After the first service is complete, the job must be closed immediately under &apos;Upcoming Jobs&apos;, this will help the client to receive the generated 
            service agreement contract and the service record for the first service.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FaCalendarCheck className="text-blue-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">2. Monitoring Service Schedule</h2>
          </div>
          <p className="text-slate-600">
            The service provider is responsible for monitoring the subsequent service schedule of packages and ensuring that the services are carried out on time.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <FaBell className="text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">3. Service Reminder and Adjusting Schedules</h2>
          </div>
          <div className="text-slate-600 space-y-4">
            <p>
              Next service dates are automatically generated by the platform based on the clients package. The service provider should send reminders 
              to clients at least 7 days before the next service date on the package and wait for a confirmation from client. The client may request a change of date from 
              the platform subject to the service provider&apos;s approval. If you have acquired the agreement on the next service date from the client over a call or 
              messaging platform, you may proceed to update the service date from your side.
            </p>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Sample reminder message:</p>
              <div className="text-sm text-slate-600 italic border-l-4 border-indigo-400 pl-4">
                Hello [name],<br/>
                As per your packaged service with us, your next servicing is scheduled for [date]. Please confirm the date. If you wish to request a new date please 
                request under &quot;My Packages&quot; of your account by tapping the &quot;Next Service Date&quot;, subject to approval based on our availability.<br/>
                Thank You
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <FaPlusCircle className="text-purple-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">4. New Job Request</h2>
          </div>
          <p className="text-slate-600">
            If additional work is needed during a subsequent service, the service provider must send a new job request to the client for approval and proceed upon approval.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <FaClock className="text-teal-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">5. Timely and Satisfactory Service</h2>
          </div>
          <p className="text-slate-600">
            The service provider must provide clients with timely and satisfactory services.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <FaClipboardCheck className="text-orange-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">6. Service Record for Subsequent Services</h2>
          </div>
          <p className="text-slate-600">
            After a service is complete for subsequent services, the service provider is responsible for marking the service as &apos;Complete&apos; with appropriate service 
            remarks for the service record.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <FaComments className="text-sky-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">7. Responding to Inquiries</h2>
          </div>
          <p className="text-slate-600">
            The service provider should respond to any client inquiries through the chat function provided on the StringsSG platform.
          </p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <FaExclamationTriangle className="text-red-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">8. Breach of Terms</h2>
          </div>
          <p className="text-red-700 font-medium">
            Charging clients outside of the knowledge of StringsSG is a breach of terms and is not allowed.
          </p>
        </div>
      </LegalPageLayout>

      <Script 
        id="google-tagmanager-new"
        src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
        strategy="lazyOnload"
        onLoad={() => {              
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-QD1HGGHDYJ');
        }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
        {`!function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '128634041116395');
          fbq('track', 'PageView');`}
      </Script>
    </>
  )
}
